import { CommonSectionTitle } from '@Components/common';
import { CenteredSpinnerWrapper, Spinner } from '@Components/common/Spinner';
import ModelCard from '@Components/ModelCard';
import Tag from '@Components/Tag';
import { _GetCarsList, _SaveCarToProfile } from '@Services/Car';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';
import { Dispatch, RootState } from 'store';
import { Col, Container, Row } from 'styled-bootstrap-grid';
import { Car } from 'types/Car';
import {
  CarMake,
  InnerWalkthrough,
  ListingBrands,
  ListingYears,
  SearchInputWrapper,
  SearchLabel,
  SelectCarSearch,
  WalkthroughFormWrapper,
} from './WalkthroughForm.styled';

type Props = {};

type QueryParams = {
  make__id?: string;
};

export default function WalkthroughForm({}: Props) {
  const { t } = useTranslation('Walkthrough');
  const { query, push } = useRouter();
  const queryParams = query as QueryParams;
  const { data, isLoading } = useSelector((state: RootState) => state.carSpecs);
  const passengerCars = data?.find((car) => car.id === 4);
  const [carMake, setCarMake] = useState<number>(Number(queryParams?.make__id) as any);
  const [carModel, setCarModel] = useState<number>();
  const [carYear, setCarYear] = useState<number>();
  const [isLocalLoading, setIsLocalLoading] = useState(false);
  const [carList, setCarList] = useState<null | Car[]>(null);
  const [isFormLoading, setIsFormLoading] = useState(false);
  const { profileCars: profileCarsModel } = useDispatch<Dispatch>();

  const getSearchCarMakers = () => {
    if (passengerCars) {
      return passengerCars.car_make.map((carMake) => ({
        label: carMake.name,
        value: carMake.id,
      }));
    } else {
      return [];
    }
  };

  const getCarModels = () => {
    const carmakeModels = passengerCars?.car_make.find((maker) => maker.id === carMake)?.car_model;
    if (carmakeModels) {
      return carmakeModels.map((model) => ({
        label: model.name,
        value: model.id,
      }));
    } else {
      return [];
    }
  };

  const getTheCarList = useCallback(async (car_type__id, make__id, model__id, year__id) => {
    try {
      setIsLocalLoading(true);
      const data = await _GetCarsList({
        car_type__id,
        make__id,
        model__id,
        year__id,
      });
      setCarList(data.results);
      setIsLocalLoading(false);
    } catch (error) {
      console.log(error);
    }
  }, []);

  const onSelectCar = (carId: number) => {
    setIsFormLoading(true);
    _SaveCarToProfile(carId).then(() => {
      profileCarsModel.getProfileCars().then(() => {
        setIsFormLoading(false);
        push(`/products/car/${carId}`);
      });
    });
  };

  useEffect(() => {
    if (passengerCars && carMake && carModel && carYear) {
      getTheCarList(passengerCars.id, carMake, carModel, carYear);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carYear]);

  useEffect(() => {
    if (carModel) {
      setCarYear(undefined);
      scroller.scrollTo('year-select', {
        duration: 400,
        offset: -120,
        delay: 0,
        smooth: 'easeInOutQuart',
      });
    }
  }, [carModel]);

  return (
    <WalkthroughFormWrapper>
      {isLoading ? (
        <CenteredSpinnerWrapper
          style={{
            paddingTop: 30,
          }}
        >
          <Spinner />
        </CenteredSpinnerWrapper>
      ) : (
        <Container>
          <Row>
            <Col col={12}>
              <InnerWalkthrough isDisabled={isFormLoading}>
                {!carMake ? (
                  <Fragment>
                    <CommonSectionTitle>{t('discover-car')}</CommonSectionTitle>
                    <SearchInputWrapper>
                      <SearchLabel>{t('choose-car-brand')}</SearchLabel>
                      <SelectCarSearch
                        isClearable={false}
                        options={getSearchCarMakers()}
                        onChange={(value: any) => {
                          setCarMake(value.value);
                          return value;
                        }}
                      />
                    </SearchInputWrapper>
                    <ListingBrands>
                      {passengerCars?.car_make.map((carMaker) => (
                        <CarMake key={carMaker.id} onClick={() => setCarMake(carMaker.id)}>
                          {carMaker?.image ? (
                            <Image src={carMaker?.image} alt={carMaker.name} layout="fill" objectFit="contain" />
                          ) : (
                            carMaker?.name
                          )}
                        </CarMake>
                      ))}
                    </ListingBrands>
                  </Fragment>
                ) : (
                  <Fragment>
                    <CommonSectionTitle>{t('discover-car')}</CommonSectionTitle>
                    <SearchInputWrapper>
                      <SearchLabel>{t('choose-car-model')}</SearchLabel>
                      <SelectCarSearch
                        isClearable={false}
                        options={getCarModels()}
                        onChange={(value: any) => {
                          setCarModel(value.value);
                          return value;
                        }}
                      />
                    </SearchInputWrapper>
                    <ListingBrands>
                      {passengerCars?.car_make
                        .find((carmake) => carmake.id === carMake)
                        ?.car_model.map((model) => (
                          <ModelCard key={model.id} data={model} onClick={() => setCarModel(model.id)} />
                        ))}
                    </ListingBrands>
                    {carModel && (
                      <Fragment>
                        <SearchInputWrapper className="year-select">
                          <SearchLabel>{t('choose-car-year')}</SearchLabel>
                        </SearchInputWrapper>
                        <ListingYears>
                          {passengerCars?.car_make
                            .find((maker) => maker.id === carMake)
                            ?.car_model.find((model) => model.id === carModel)
                            ?.available_years.map((year) => (
                              <Tag
                                key={carModel + carMake + year.id}
                                isActive={carYear === Number(year.id)}
                                name={year.year.toString()}
                                isWhite
                                onClick={() => {
                                  setCarYear(Number(year.id));
                                }}
                              />
                            ))}
                        </ListingYears>
                        {carYear && (
                          <Fragment>
                            <SearchInputWrapper>
                              <SearchLabel>{t('choose-transmission')}</SearchLabel>
                            </SearchInputWrapper>
                            <ListingYears>
                              {isLocalLoading || !Boolean(carList?.length) ? (
                                <Spinner />
                              ) : (
                                (carList as Car[]).map((car) => (
                                  <Tag
                                    key={car.id + carMake + carModel}
                                    isActive={false}
                                    name={car.transmission}
                                    isWhite
                                    onClick={() => onSelectCar(car.id)}
                                  />
                                ))
                              )}
                            </ListingYears>
                            {isFormLoading && (
                              <SearchInputWrapper
                                style={{
                                  justifyContent: 'center',
                                  margin: '30px 0',
                                }}
                              >
                                <Spinner />
                              </SearchInputWrapper>
                            )}
                          </Fragment>
                        )}
                      </Fragment>
                    )}
                  </Fragment>
                )}
              </InnerWalkthrough>
            </Col>
          </Row>
        </Container>
      )}
    </WalkthroughFormWrapper>
  );
}
